import { headers } from "next/headers";
import { redirect, permanentRedirect } from "next/navigation";
import Link from "next/link";
import { prisma } from "@/lib/db";
import { track404 } from "@/lib/seo/monitor-404";

/**
 * Root not-found handler.
 *
 * Before showing the 404 page, look up the requested path in the Redirect
 * table. If we have a match, fire a permanent/temporary redirect from the
 * server, increment hits, and never reach the user-visible 404.
 *
 * Note: Next 16 doesn't pass the request URL into not-found.tsx directly,
 * so we read x-finzone-pathname injected by middleware (or fall back to
 * referer).
 */
export default async function NotFound() {
  const h = await headers();
  let path = h.get("x-finzone-pathname") || h.get("x-pathname") || "";
  if (!path) {
    const ref = h.get("referer");
    if (ref) {
      try {
        path = new URL(ref).pathname;
      } catch {}
    }
  }
  if (path && path !== "/") {
    const norm = path.length > 1 && path.endsWith("/") ? path.replace(/\/+$/, "") : path;
    const r = await prisma.redirect.findFirst({
      where: { fromPath: norm, enabled: true },
      select: { toPath: true, statusCode: true, id: true },
    });
    if (r) {
      // Fire-and-forget hit counter
      prisma.redirect
        .update({
          where: { id: r.id },
          data: { hits: { increment: 1 }, lastHitAt: new Date() },
        })
        .catch(() => {});
      // 308 / 301 → permanent; 307 / 302 → temporary
      if (r.statusCode === 301 || r.statusCode === 308) permanentRedirect(r.toPath);
      redirect(r.toPath);
    }
    // No redirect — log the miss for /admin/404-monitor
    track404({
      path: norm,
      referrer: h.get("referer"),
      userAgent: h.get("user-agent"),
      ip:
        h.get("x-forwarded-for")?.split(",")[0]?.trim() ??
        h.get("x-real-ip") ??
        null,
    }).catch(() => {});
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20 text-center">
      <div className="text-7xl font-display font-bold mb-4 text-ink-muted">404</div>
      <h1 className="text-2xl font-display font-semibold mb-2">Không tìm thấy trang</h1>
      <p className="text-sm text-ink-muted max-w-md mb-8">
        Trang bạn đang tìm không tồn tại hoặc đã bị di chuyển.
      </p>
      <Link
        href="/"
        className="px-5 py-2.5 rounded-lg bg-accent-primary text-white text-sm font-medium hover:bg-accent-primary/90 transition"
      >
        ← Về trang chủ
      </Link>
    </div>
  );
}
